import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField, Typography } from '@mui/material';
import api from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import { DOCUMENT, documentSolidButtonSx } from '../constants/documentTheme';
import { MEMORIES_GUEST_PROFILE_KEY, MEMORIES_GUEST_TOKEN_KEY } from '../utils/memoriesHost';
import { memoriesAuthEntryUrls } from '../utils/memoriesAuthBridge';

const outlineButtonSx = {
  display: 'block',
  textAlign: 'center',
  width: '100%',
  backgroundColor: '#fff',
  color: DOCUMENT.ink2,
  border: `1px solid ${DOCUMENT.rule}`,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  py: 1.25,
  borderRadius: '2px',
  '&:hover': { backgroundColor: DOCUMENT.surface, color: DOCUMENT.ink },
};

/**
 * Share / QR entry: signed-in users join in one tap; everyone else can
 * sign in, create an account, or continue as a guest with name + email.
 */
const MemoriesGuestGate = ({
  inviteCode,
  onJoined,
  returnUrl,
  titleKey = 'memories.joinAlbumTitle',
  bodyKey = 'memories.joinAlbumBody',
  fieldSx = {},
  showGuestForm = true,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const authUrls = useMemo(
    () => memoriesAuthEntryUrls(
      returnUrl
        || (typeof window !== 'undefined' ? window.location.href : '/')
    ),
    [returnUrl]
  );

  const finishJoin = (data) => {
    if (data.guestToken) {
      localStorage.setItem(MEMORIES_GUEST_TOKEN_KEY, data.guestToken);
      localStorage.setItem(
        MEMORIES_GUEST_PROFILE_KEY,
        JSON.stringify({ displayName, email })
      );
      api.defaults.headers.common['X-Guest-Token'] = data.guestToken;
    }
    onJoined?.(data);
  };

  const joinAsSignedIn = async () => {
    if (!inviteCode) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post(`/memories/invites/${inviteCode}/accept`);
      finishJoin(data);
    } catch (err) {
      setError(err.response?.data?.error || t('memories.joinFailed'));
    } finally {
      setSaving(false);
    }
  };

  const joinAsGuest = async (event) => {
    event.preventDefault();
    if (!inviteCode) return;
    setSaving(true);
    setError('');
    try {
      const { data } = await api.post(`/memories/invites/${inviteCode}/accept`, {
        displayName,
        email,
      });
      finishJoin(data);
    } catch (err) {
      setError(err.response?.data?.error || t('memories.joinFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${DOCUMENT.rule}`,
        backgroundColor: DOCUMENT.surface,
        p: 2.5,
        mb: 2,
      }}
      data-testid="memories-guest-gate"
    >
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 22, color: DOCUMENT.ink, mb: 0.5 }}>
        {t(titleKey)}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
        {user
          ? t('memories.joinAlbumSignedInBody', {
            name: [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
              || user.username
              || user.email,
          })
          : t(bodyKey)}
      </Typography>

      {error && <Typography sx={{ color: '#9C3B2A', mb: 2 }}>{error}</Typography>}

      {user ? (
        <Button
          type="button"
          disabled={saving || !inviteCode}
          onClick={joinAsSignedIn}
          sx={{
            ...documentSolidButtonSx,
            '&.Mui-disabled': { backgroundColor: DOCUMENT.oxblood, color: DOCUMENT.ivory, opacity: 0.45 },
          }}
        >
          {saving ? t('common.loading') : t('memories.continueWithAccount')}
        </Button>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
          <Button
            component={authUrls.login.startsWith('http') ? 'a' : RouterLink}
            href={authUrls.login.startsWith('http') ? authUrls.login : undefined}
            to={authUrls.login.startsWith('http') ? undefined : authUrls.login}
            sx={{
              ...documentSolidButtonSx,
              textAlign: 'center',
              width: '100%',
              py: 1.25,
            }}
          >
            {t('memories.signIn')}
          </Button>
          <Button
            component={authUrls.register.startsWith('http') ? 'a' : RouterLink}
            href={authUrls.register.startsWith('http') ? authUrls.register : undefined}
            to={authUrls.register.startsWith('http') ? undefined : authUrls.register}
            sx={outlineButtonSx}
          >
            {t('memories.createAccountToJoin')}
          </Button>

          {showGuestForm && (
            <Box component="form" onSubmit={joinAsGuest} sx={{ mt: 1.5, pt: 2, borderTop: `1px solid ${DOCUMENT.rule}` }}>
              <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, color: DOCUMENT.ink, mb: 0.5 }}>
                {t('memories.continueAsGuest')}
              </Typography>
              <Typography sx={{ color: DOCUMENT.ink3, fontSize: 13, mb: 2 }}>
                {t('memories.guestUploadHint')}
              </Typography>
              <TextField
                required
                fullWidth
                label={t('memories.guestName')}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                sx={{ ...fieldSx, mb: 2 }}
              />
              <TextField
                required
                fullWidth
                type="email"
                label={t('memories.guestEmail')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ ...fieldSx, mb: 2 }}
              />
              <Button
                type="submit"
                disabled={saving || !inviteCode}
                sx={{
                  ...outlineButtonSx,
                  '&.Mui-disabled': { opacity: 0.45 },
                }}
              >
                {saving ? t('common.loading') : t('memories.continueAsGuest')}
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default MemoriesGuestGate;
